import * as RadixTooltip from '@radix-ui/react-tooltip';
import cn from 'classnames';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { FC, useCallback, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { SlotContext } from './SlotContext';
import styles from './SlotNode.module.scss';

const messages = defineMessages({
  instructions: {
    id: 'slotNode.instructions',
    defaultMessage: 'Instructions',
  },
  emptyValue: {
    id: 'slotNode.emptyValue',
    defaultMessage: '(empty {index})',
  },
});

type SerializedSlotNode = Spread<
  {
    slotName: string;
  },
  SerializedLexicalNode
>;

const SlotBadge: FC<{ slotName: string; nodeKey: NodeKey }> = ({
  slotName,
}) => {
  const { slots, slotInstructions, onSlotClick, readOnly } =
    useContext(SlotContext);
  const { formatMessage } = useIntl();

  const slotValues = slots?.[slotName] || [];
  const instructionText = slotInstructions?.[slotName]?.trim() ?? '';
  const hasInstructions = instructionText.length > 0;

  const displayValue = slotValues[0] || slotName;
  const moreCount = slotValues.length > 1 ? slotValues.length - 1 : 0;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!readOnly) {
        onSlotClick(slotName);
      }
    },
    [slotName, onSlotClick, readOnly],
  );

  const badge = (
    <span
      className={cn(styles.slotBadge, readOnly && styles.slotBadgeReadOnly)}
      onClick={handleClick}
    >
      <span className={styles.slotValue}>{displayValue}</span>
      {moreCount > 0 && <span className={styles.moreCount}>+{moreCount}</span>}
      {hasInstructions && (
        <span aria-hidden className={styles.instructionsIndicator}>
          🔧
        </span>
      )}
    </span>
  );

  const hasTooltip = slotValues.length > 0 || hasInstructions;

  if (!hasTooltip) {
    return badge;
  }

  return (
    <RadixTooltip.Root delayDuration={0}>
      <RadixTooltip.Trigger asChild>{badge}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          className={styles.tooltipContent}
          side="top"
          sideOffset={4}
        >
          {slotValues.length > 0 && (
            <div className={styles.tooltipSection}>
              {slotValues.map((value, i) => (
                <div key={i}>
                  {value ||
                    formatMessage(messages.emptyValue, { index: i + 1 })}
                </div>
              ))}
            </div>
          )}
          {hasInstructions && (
            <div className={styles.tooltipSection}>
              <div className={styles.tooltipSectionTitle}>
                {formatMessage(messages.instructions)}
              </div>
              <div className={styles.tooltipInstructions}>
                {instructionText}
              </div>
            </div>
          )}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  );
};

export class SlotNode extends DecoratorNode<JSX.Element> {
  __slotName: string;

  static getType(): string {
    return 'slot';
  }

  static clone(node: SlotNode): SlotNode {
    return new SlotNode(node.__slotName, node.__key);
  }

  constructor(slotName: string, key?: NodeKey) {
    super(key);
    this.__slotName = slotName;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.className = styles.slotNodeWrapper;
    return span;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedSlotNode): SlotNode {
    return new SlotNode(serializedNode.slotName);
  }

  exportJSON(): SerializedSlotNode {
    return {
      type: 'slot',
      version: 1,
      slotName: this.__slotName,
    };
  }

  getSlotName(): string {
    return this.__slotName;
  }

  getTextContent(): string {
    return `{{${this.__slotName}}}`;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  decorate(): JSX.Element {
    return <SlotBadge slotName={this.__slotName} nodeKey={this.__key} />;
  }
}

export const $createSlotNode = (slotName: string): LexicalNode => {
  return new SlotNode(slotName);
};
